import { useEffect, useState } from "react";
import { api, fmtR, getToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import StatusBadge from "@/components/StatusBadge";
import { Download } from "lucide-react";

export default function Reports() {
    const [tab, setTab] = useState("vouchers");
    const [filters, setFilters] = useState({ status: "all", cost_shop_id: "all", branch_id: "all", from_date: "", to_date: "" });
    const [vouchers, setVouchers] = useState([]);
    const [reds, setReds] = useState([]);
    const [costShops, setCostShops] = useState([]);
    const [branches, setBranches] = useState([]);

    useEffect(() => {
        api.get("/admin/cost-shops").then((r) => setCostShops(r.data));
        api.get("/admin/branches").then((r) => setBranches(r.data));
    }, []);

    const params = () => {
        const p = {};
        if (filters.status !== "all") p.status = filters.status;
        if (filters.cost_shop_id !== "all") p.cost_shop_id = filters.cost_shop_id;
        if (filters.branch_id !== "all") p.branch_id = filters.branch_id;
        if (filters.from_date) p.from_date = filters.from_date;
        if (filters.to_date) p.to_date = filters.to_date;
        return p;
    };

    const load = async () => {
        if (tab === "vouchers") {
            const { data } = await api.get("/admin/reports/vouchers", { params: params() });
            setVouchers(data);
        } else {
            const { data } = await api.get("/admin/reports/redemptions", { params: params() });
            setReds(data);
        }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab, filters]);

    const exportCsv = () => {
        const endpoint = tab === "vouchers" ? "/admin/reports/vouchers" : "/admin/reports/redemptions";
        const p = params(); p.format = "csv";
        const qs = new URLSearchParams(p).toString();
        const token = getToken();
        fetch(`${api.defaults.baseURL}${endpoint}?${qs}`, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => r.blob()).then((b) => {
                const url = URL.createObjectURL(b);
                const a = document.createElement("a");
                a.href = url; a.download = `${tab}-report.csv`; a.click();
                URL.revokeObjectURL(url);
            });
    };

    return (
        <div className="p-8 max-w-7xl" data-testid="reports-page">
            <div className="mb-6">
                <div className="text-xs tracking-widest text-[#7A726D] uppercase">Insights</div>
                <h1 className="font-display text-4xl font-bold text-espresso tracking-tight">Reports</h1>
            </div>

            <Card className="border-[#E5E0D8] shadow-none rounded-sm mb-4">
                <CardContent className="p-4 grid grid-cols-2 md:grid-cols-6 gap-3 items-end">
                    {tab === "vouchers" && (
                        <div>
                            <Label className="text-xs uppercase text-[#7A726D]">Status</Label>
                            <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
                                <SelectTrigger className="rounded-sm mt-1" data-testid="filter-status"><SelectValue/></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All</SelectItem>
                                    <SelectItem value="active">Active</SelectItem>
                                    <SelectItem value="redeemed">Redeemed</SelectItem>
                                    <SelectItem value="expired">Expired</SelectItem>
                                    <SelectItem value="void">Void</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    )}
                    {tab === "redemptions" && (
                        <div>
                            <Label className="text-xs uppercase text-[#7A726D]">Branch</Label>
                            <Select value={filters.branch_id} onValueChange={(v) => setFilters({ ...filters, branch_id: v })}>
                                <SelectTrigger className="rounded-sm mt-1" data-testid="filter-branch"><SelectValue/></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All</SelectItem>
                                    {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                    )}
                    <div>
                        <Label className="text-xs uppercase text-[#7A726D]">Cost shop</Label>
                        <Select value={filters.cost_shop_id} onValueChange={(v) => setFilters({ ...filters, cost_shop_id: v })}>
                            <SelectTrigger className="rounded-sm mt-1" data-testid="filter-cost-shop"><SelectValue/></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All</SelectItem>
                                {costShops.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div><Label className="text-xs uppercase text-[#7A726D]">From</Label><Input type="date" value={filters.from_date} onChange={(e) => setFilters({ ...filters, from_date: e.target.value })} className="rounded-sm mt-1" data-testid="filter-from"/></div>
                    <div><Label className="text-xs uppercase text-[#7A726D]">To</Label><Input type="date" value={filters.to_date} onChange={(e) => setFilters({ ...filters, to_date: e.target.value })} className="rounded-sm mt-1" data-testid="filter-to"/></div>
                    <Button onClick={exportCsv} className="bg-espresso rounded-sm" data-testid="export-csv-button"><Download className="w-4 h-4 mr-2"/>Export CSV</Button>
                </CardContent>
            </Card>

            <Tabs value={tab} onValueChange={setTab}>
                <TabsList className="bg-white border border-[#E5E0D8] rounded-sm">
                    <TabsTrigger value="vouchers" data-testid="tab-vouchers">Vouchers</TabsTrigger>
                    <TabsTrigger value="redemptions" data-testid="tab-redemptions">Redemptions</TabsTrigger>
                </TabsList>
                <TabsContent value="vouchers">
                    <Card className="border-[#E5E0D8] shadow-none rounded-sm mt-3">
                        <CardContent className="p-0">
                            <Table data-testid="report-vouchers-table">
                                <TableHeader><TableRow className="bg-[#F9F8F6]"><TableHead>Serial</TableHead><TableHead>Name</TableHead><TableHead>Type</TableHead><TableHead>Value</TableHead><TableHead>Remaining</TableHead><TableHead>Status</TableHead><TableHead>Redeemed</TableHead><TableHead>Last redeemed</TableHead><TableHead>At branch(es)</TableHead><TableHead>Created</TableHead></TableRow></TableHeader>
                                <TableBody>
                                    {vouchers.length === 0 && <TableRow><TableCell colSpan={10} className="text-center text-[#7A726D] py-8">No data</TableCell></TableRow>}
                                    {vouchers.map((v) => (
                                        <TableRow key={v.id}>
                                            <TableCell className="font-mono text-xs">{v.serial}</TableCell>
                                            <TableCell>{v.name}</TableCell>
                                            <TableCell className="capitalize">{v.type}</TableCell>
                                            <TableCell>{fmtR(v.value_amount || v.retail_value)}</TableCell>
                                            <TableCell>{fmtR(v.remaining_value)}</TableCell>
                                            <TableCell><StatusBadge status={v.status}/></TableCell>
                                            <TableCell className="text-center">{v.redemption_count || 0}</TableCell>
                                            <TableCell className="text-xs whitespace-nowrap">
                                                {v.last_redeemed_at ? v.last_redeemed_at.slice(0, 19).replace("T", " ") : "—"}
                                            </TableCell>
                                            <TableCell className="text-xs max-w-[180px] truncate" title={v.redeemed_branches || ""}>
                                                {v.redeemed_branches || "—"}
                                            </TableCell>
                                            <TableCell className="text-xs">{v.created_at?.slice(0, 10)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>
                </TabsContent>
                <TabsContent value="redemptions">
                    <Card className="border-[#E5E0D8] shadow-none rounded-sm mt-3">
                        <CardContent className="p-0">
                            <Table data-testid="report-redemptions-table">
                                <TableHeader><TableRow className="bg-[#F9F8F6]"><TableHead>When</TableHead><TableHead>Voucher</TableHead><TableHead>Branch</TableHead><TableHead>Receipt</TableHead><TableHead>Amount</TableHead><TableHead>Source</TableHead></TableRow></TableHeader>
                                <TableBody>
                                    {reds.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-[#7A726D] py-8">No data</TableCell></TableRow>}
                                    {reds.map((r) => (
                                        <TableRow key={r.id}>
                                            <TableCell className="text-xs">{r.redeemed_at?.slice(0, 19).replace("T", " ")}</TableCell>
                                            <TableCell className="font-mono text-xs">{r.voucher_serial}</TableCell>
                                            <TableCell>{r.branch_name}</TableCell>
                                            <TableCell className="font-mono text-xs">{r.receipt_number}</TableCell>
                                            <TableCell>{fmtR(r.amount_redeemed)}</TableCell>
                                            <TableCell className="capitalize text-xs">{r.source}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>
        </div>
    );
}
